import classnames from 'classnames';
import Quill, { QuillOptions } from 'quill';
import { default as Delta, default as QuillDelta } from 'quill-delta';
import { ComponentProps, useEffect, useRef } from 'react';
import { Box, BoxProps, BoxType } from 'reflexbox';

function useQuill({
	theme,
	value,
	onChange,
	onQuill,
	readOnly,
	autoFocus,
	placeholder,
}: {
	theme?: string;
	value: QuillDelta;
	onChange?: (value: QuillDelta) => void;
	onQuill?: (quill?: Quill) => void;
	readOnly?: boolean;
	autoFocus?: boolean;
	placeholder?: string;
}) {
	// Reference to the div element for binding Quill
	const ref = useRef<HTMLDivElement>(null);
	// Store the quill instance to pass with `onQuill` to allow external components to edit it's state.
	const quill = useRef<[Quill] | []>([]);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		if (quill.current[0]) {
			return;
		}

		const options: QuillOptions = {
			theme: theme ?? 'snow',
			readOnly,
			placeholder,
		};

		if (theme === 'bubble') {
			options.modules = {
				toolbar: false,
			};
			options.formats = ['bold', 'italic', 'underline', 'align', 'link', 'size', 'list'];
		} else {
			options.modules = { toolbar: false };
			options.formats = [
				'bold',
				'italic',
				'link',
				'list',
				'underline',
				'image',
				'video',
				'header',
				'indent',
			];
		}

		quill.current[0] = new Quill(ref.current!, options);

		quill.current[0].setContents(value, 'api');

		if (autoFocus) {
			quill.current[0].focus();
		}
		if (onChange) {
			quill.current[0].on('text-change', (_a, _b, source) => {
				if (source === 'api') {
					return;
				}
				onChange?.(quill.current[0]!.getContents() as QuillDelta);
			});
		}

		onQuill?.(quill.current[0]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { ref, quill: quill.current[0] };
}

export function QuillEditor({
	autoFocus,
	value,
	onChange,
	onKeyDown,
	onFocus,
	onBlur,
	inline,
	readOnly,
	wide,
	onQuill,
	placeholder,
	className,
	theme,
	...rest
}: BoxProps & {
	autoFocus?: boolean;
	value?: QuillDelta;
	onChange?: (value: QuillDelta) => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
	onFocus?: () => void;
	onBlur?: () => void;
	inline?: boolean;
	readOnly?: boolean;
	wide?: boolean;
	onQuill?: (quill?: Quill) => void;
	placeholder?: string;
	className?: string;
	theme?: string;
}) {
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	const { ref, quill } = useQuill({
		theme: theme ?? 'snow',
		value: value ?? new Delta(),
		onChange: (value) => onChangeRef.current?.(value),
		onQuill,
		readOnly,
		autoFocus,
		placeholder,
	});

	useEffect(() => {
		if (autoFocus && quill) {
			quill.focus();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [autoFocus]);

	return (
		<div
			className={classnames({
				'ql-boxed': true,
				'ql-readonly': readOnly,
				'ql-inline': inline,
				'ql-wide': wide,
				[className || '']: Boolean(className),
			})}
		>
			<Box
				className="ql-wrapper"
				onFocus={() => {
					if (quill?.hasFocus()) {
						onFocus?.();
						quill?.focus();
					}
				}}
				onBlur={() => {
					onBlur?.();
					quill?.blur();
				}}
				onKeyDown={(event) => onKeyDown?.(event)}
			>
				<Box ref={ref} minHeight={100} {...(rest as ComponentProps<BoxType>)} />
			</Box>
		</div>
	);
}
